import * as React from 'react'
import { useEffect, useState } from 'react'
import Dropzone from 'react-dropzone'
import type { DropzoneRef } from 'react-dropzone'

import { Box, theme } from '@factorialco/deprecated-design-system'
import { TeamDocument, UpdateAvatarMutationDocument } from 'generated/resources/graphql'

import AvatarUploader from 'modules/core/components/AvatarUploader'
import Popover from 'modules/core/components/PopoverMenu'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import TeamForm from 'modules/teams/lib/forms/Team'
import type { ITeam } from 'modules/teams/store/models/Team'

interface Props {
  team: ITeam
}

const AvatarForm: React.FC<Props> = ({ team }) => {
  const [isDragging, setIsDragging] = useState(false)
  const dropzoneRef = React.createRef<DropzoneRef>()
  const [mutate, { error }] = useMutation(UpdateAvatarMutationDocument, {
    extractorFn: (data) => data.teamsMutations.updateTeamsTeam,
    refetchQueries: [TeamDocument],
    awaitRefetchQueries: true,
  })
  const form = useUnsafeMemo(() => new TeamForm(team), [team])

  useEffect(() => {
    document.addEventListener(
      'dragenter',
      () => {
        setIsDragging(true)
      },
      { once: true }
    )
    document.addEventListener(
      'drop',
      () => {
        setIsDragging(false)
      },
      { once: true }
    )
  }, [])

  const onDrop = (files: File[]) => {
    form.get('avatar').set(files[0])
    onSubmit()
  }

  const onSubmit = async () => {
    await mutate({
      variables: {
        id: team.id,
        avatar: form.get('avatar').value,
      },
      optimisticResponse: {
        teamsMutations: {
          updateTeamsTeam: {
            errors: [],
            team: {
              ...team,
              avatar: form.get('avatar').value,
            },
          },
        },
      },
    })
  }

  const openDropzone = () => {
    dropzoneRef.current?.open()
  }

  const onDeleteAvatar = () => {
    form.setValues({
      avatar: null,
    })
    onSubmit()
  }

  const noAvatarUploaded = !form.get('avatar').value
  return (
    <>
      <Dropzone onDrop={onDrop} multiple={false} ref={dropzoneRef} noClick noKeyboard>
        {({ getRootProps, getInputProps, isDragActive }) => (
          <div
            {...getRootProps()}
            style={{
              borderRadius: theme.borderRadius.abs008,
              borderWidth: theme.border.width.s4,
              borderColor: isDragActive || isDragging ? theme.colors.grey400 : 'transparent',
              borderStyle: 'dashed',
            }}
          >
            {noAvatarUploaded ? (
              <AvatarUploader size="s120" avatarField={form.get('avatar')} handleDrop={onSubmit} />
            ) : (
              <Popover onClick={() => track(trackingEvents.teams.goToUploadPhoto)} type="list">
                <Popover.Trigger>
                  <AvatarUploader size="s120" avatarField={form.get('avatar')} readonly />
                </Popover.Trigger>
                <Popover.Content side="top" align="center">
                  <Popover.TouchableOption
                    title={i18n.t('employee_form.avatar.new')}
                    onClick={openDropzone}
                  />
                  <Popover.TouchableOption
                    title={i18n.t('employee_form.avatar.delete')}
                    onClick={onDeleteAvatar}
                    danger
                  />
                </Popover.Content>
              </Popover>
            )}
            {error && <Box>{error.message}</Box>}
            <input {...getInputProps()} />
          </div>
        )}
      </Dropzone>
    </>
  )
}

export default AvatarForm
